<template>
  <span
    v-if="label"
    class="tw-rounded tw-font-medium tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
    :class="[badgeClass, small ? 'tw-text-[10px] tw-px-1 tw-py-px' : 'tw-text-xs tw-px-2 tw-py-0.5']"
    :title="tooltip"
  >{{ label }}</span>
</template>

<script setup>
import { computed } from 'vue'
import { formatDateTimeNL } from './formatters'

// How the meter is read, from /eleena/api/v1/meters/{serial} or a row of /eleena/api/v2/meters:
//   sources ['dlms']        polling server (DLMS)
//   sources ['c4a']         Cast4all, account Staedion or Solarminds (c4a_account)
//   sources ['c4a', 'dlms'] migrated from Cast4all to DLMS, now read via DLMS
//   sources ['solaredge']   SolarEdge monitoring API (switched from Cast4all on 2026-11-01)
const props = defineProps({
  meter: { type: Object, default: null },
  small: { type: Boolean, default: false },
})

const C4A_ACCOUNT_LABELS = { staedion: 'Staedion', solarminds: 'Solarminds' }

const sources = computed(() => props.meter?.sources ?? [])
const isDlms = computed(() => sources.value.includes('dlms'))
const isC4a = computed(() => sources.value.includes('c4a'))
const isSolarEdge = computed(() => sources.value.includes('solaredge'))
// the list calls it c4a_external_id, the detail external_id
const externalId = computed(() => props.meter?.c4a_external_id ?? props.meter?.external_id)
const accountLabel = computed(() => C4A_ACCOUNT_LABELS[props.meter?.c4a_account] ?? props.meter?.c4a_account ?? '')

const label = computed(() => {
  if (isDlms.value) return 'Polling'
  if (isSolarEdge.value) return 'SolarEdge'
  if (isC4a.value) return 'C4A'
  return ''
})

const badgeClass = computed(() => {
  if (isDlms.value) return 'tw-bg-blue-100 tw-text-blue-700'
  if (isSolarEdge.value) return 'tw-bg-red-100 tw-text-red-700'
  return 'tw-bg-purple-100 tw-text-purple-700'
})

const tooltip = computed(() => {
  if (isDlms.value && isC4a.value) {
    return `Polling server (DLMS), gemigreerd van Cast4all${accountLabel.value ? ` (${accountLabel.value})` : ''}`
  }
  if (isDlms.value) return 'Polling server (DLMS)'
  if (isSolarEdge.value) return `SolarEdge monitoring${externalId.value && props.meter?.source === 'solaredge' ? `, site ${externalId.value}` : ''}`
  const lastSeen = props.meter?.last_successful_communication
  return [
    `Cast4all, account ${accountLabel.value || 'onbekend'}`,
    externalId.value ? `Cast4all-id ${externalId.value}` : null,
    `Laatste communicatie: ${lastSeen ? formatDateTimeNL(lastSeen) : 'onbekend'}`,
  ].filter(Boolean).join('\n')
})
</script>
